import { useEffect, useRef, useState } from 'react'
import { IconFileText, IconNotebook, IconPin, IconPinFilled, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { folderApi, noteApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import type { NoteSummary } from '@/api/types'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { TrashDrawer } from '@/components/common/TrashDrawer'
import { FolderTree, gorunenYol } from '@/components/common/FolderTree'
import { Pager } from '@/components/common/Pager'
import { formatRelative } from '@/utils/format'
import { tr } from '@/i18n/tr'
import { useProject } from '@/hooks/usePermissions'
import { Avatar, Button, IconButton, TextField, notifications, useDisclosure } from '@/ui'
import './notes.css'

const SAYFA_BOYU = 50

export function NotesListPage() {
  const { project, can } = useProject()
  const canEdit = can('notes.edit')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [copOpened, cop] = useDisclosure(false)
  // null = tüm notlar. Klasör seçimi hem listeyi hem yeni notun yerini belirler.
  const [klasorId, setKlasorId] = useState<string | null>(null)
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['notes', project.id, q, klasorId, page],
    queryFn: () => noteApi.list(project.id, {
      q: q || undefined, folderId: klasorId ?? undefined, page, size: SAYFA_BOYU,
    }),
  })
  const klasorler = useQuery({ queryKey: ['folders', project.id], queryFn: () => folderApi.list(project.id) })
  const toplam = useQuery({
    queryKey: ['note-count', project.id],
    queryFn: () => noteApi.list(project.id, { size: 1 }).then((p) => p.totalElements),
  })

  const create = useMutation({
    mutationFn: () => noteApi.create(project.id, {
      title: tr.note.untitled, folderId: klasorId ?? undefined,
    }),
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ['notes', project.id] })
      qc.invalidateQueries({ queryKey: ['note-count', project.id] })
      navigate(`/projeler/${project.id}/notlar/${n.id}`)
    },
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  /* Başlıktaki "Yeni not" ve komut paleti buraya ?yeni=1 ile gelir; not seçili klasörde
     açılır. Tek seferlik: StrictMode'un çift effect'i iki not oluşturmasın. */
  const yeniIstendi = useRef(false)
  useEffect(() => {
    if (params.get('yeni') !== '1') { yeniIstendi.current = false; return }
    params.delete('yeni')
    setParams(params, { replace: true })
    if (!canEdit || yeniIstendi.current) return
    yeniIstendi.current = true
    create.mutate()
  }, [params, setParams, canEdit, create])

  const pin = useMutation({
    mutationFn: (id: string) => noteApi.togglePin(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', project.id] }),
    onError: (e) => notifications.show({ color: 'red', message: errorMessage(e) }),
  })

  const notlar = query.data?.content ?? []
  const sabit = notlar.filter((n) => n.pinned)
  const diger = notlar.filter((n) => !n.pinned)
  const klasorAdi = (id: string | null) => (id ? gorunenYol(klasorler.data ?? [], id) : null)
  const baslik = klasorId ? klasorAdi(klasorId) : tr.common.all

  const pinDugmesi = (n: NoteSummary, dark?: boolean) => canEdit && (
    <IconButton label={n.pinned ? tr.note.unpin : tr.note.pin} size="sm" active={n.pinned}
      className={dark ? 'nx-note-pin nx-note-pin--on' : 'nx-note-pin'}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); pin.mutate(n.id) }}>
      {n.pinned ? <IconPinFilled size={15} /> : <IconPin size={16} stroke={1.8} />}
    </IconButton>
  )

  return (
    <div className="nx-with-folders">
      <FolderTree
        projectId={project.id}
        secili={klasorId}
        // Filtre değişince sayfa başa: 3. sayfada kalmak boş liste demek.
        onSec={(id) => { setKlasorId(id); setPage(0) }}
        canEdit={can('folders.manage')}
        sayac="not"
        toplam={toplam.data}
      />

      <section className="nx-notes" aria-label="Notlar">
        <div className="nx-notes__bar">
          <TextField className="nx-notes__search" type="search" aria-label="Notlarda ara" placeholder="Notlarda ara…"
            leftSection={<IconSearch size={18} stroke={1.8} aria-hidden="true" />} value={q}
            onChange={(e) => { setQ(e.currentTarget.value); setPage(0) }} />
          {query.data && (
            <span className="nx-notes__count">{baslik} · <span className="nx-nums">{query.data.totalElements}</span> not</span>
          )}
          <span style={{ flex: 1 }} />
          {can('notes.delete') && (
            <Button variant="secondary" leftIcon={<IconTrash size={17} stroke={1.8} aria-hidden="true" />} onClick={cop.open}>
              {tr.project.trash}
            </Button>
          )}
        </div>

        <TrashDrawer
          opened={copOpened}
          onClose={cop.close}
          title="Çöp kutusu · Notlar"
          queryKey={['notes-trash', project.id]}
          load={async () => {
            const page = await noteApi.trash(project.id)
            return page.content.map((n) => ({
              id: n.id,
              label: n.title,
              hint: n.excerpt || undefined,
              leading: <span className="nx-note-icon" aria-hidden="true"><IconFileText size={18} stroke={1.8} /></span>,
            }))
          }}
          restore={(id) => noteApi.restore(id)}
          onRestored={() => {
            qc.invalidateQueries({ queryKey: ['notes', project.id] })
            qc.invalidateQueries({ queryKey: ['note-count', project.id] })
          }}
        />

        {query.isLoading && <LoadingState />}
        {query.isError && <ErrorState error={query.error} onRetry={() => query.refetch()} />}

        {query.data && notlar.length === 0 && (
          <EmptyState
            icon={<IconNotebook size={24} stroke={1.6} />}
            title={q ? 'Eşleşen not yok' : klasorId ? 'Bu klasörde not yok.' : tr.note.empty}
            hint={q ? 'Farklı bir kelime deneyin.' : tr.note.emptyHint}
            action={canEdit && !q && (
              <Button leftIcon={<IconPlus size={16} stroke={2.2} aria-hidden="true" />} loading={create.isPending}
                onClick={() => create.mutate()}>
                İlk notu oluştur
              </Button>
            )}
          />
        )}

        {sabit.length > 0 && (
          <div className="nx-notes__group">
            <span className="nx-notes__label"><IconPin size={13} stroke={1.8} aria-hidden="true" />Sabitlenenler</span>
            <div className="nx-notes__pinned">
              {sabit.map((n) => (
                <article key={n.id} className="nx-row nx-note-card">
                  <div className="nx-note-card__head">
                    <Link to={`/projeler/${project.id}/notlar/${n.id}`} className="nx-note-title nx-note-card__title">{n.title}</Link>
                    {pinDugmesi(n, true)}
                  </div>
                  <p className="nx-note-card__excerpt">{n.excerpt || 'Boş not'}</p>
                  <span className="nx-note-card__meta">
                    {n.updatedByName && <Avatar name={n.updatedByName} size={22} />}
                    {n.updatedByName ? `${n.updatedByName} · ` : ''}{formatRelative(n.updatedAt)}
                  </span>
                </article>
              ))}
            </div>
          </div>
        )}

        {diger.length > 0 && (
          <div className="nx-notes__group">
            {sabit.length > 0 && <span className="nx-notes__label">Tüm notlar</span>}
            <div className="nx-panel nx-notes__list">
              {diger.map((n) => {
                const klasor = !klasorId ? klasorAdi(n.folderId) : null
                return (
                  <div key={n.id} className="nx-row nx-note-row">
                    <span className="nx-note-icon" aria-hidden="true"><IconFileText size={19} stroke={1.8} /></span>
                    <span className="nx-note-row__main">
                      <span className="nx-note-row__title">
                        <Link to={`/projeler/${project.id}/notlar/${n.id}`} className="nx-note-title">{n.title}</Link>
                        {klasor && <span className="nx-note-row__folder">{klasor}</span>}
                      </span>
                      <span className={n.excerpt ? 'nx-note-row__excerpt' : 'nx-note-row__excerpt is-empty'}>{n.excerpt || 'Boş not'}</span>
                    </span>
                    <span className="nx-note-row__meta">
                      {n.updatedByName && <Avatar name={n.updatedByName} size={24} title={n.updatedByName} />}
                      {formatRelative(n.updatedAt)}
                    </span>
                    {pinDugmesi(n)}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <Pager page={page} totalPages={query.data?.totalPages} onChange={setPage} />
      </section>
    </div>
  )
}
